import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Anchor,
  Button,
  Checkbox,
  Container,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useSubmitEmploymentApp } from '@/hooks/usePortalApi';
import type { EmploymentApplication as EmploymentApplicationType } from '@/types';

const POSITION_OPTIONS = [
  { value: 'driver', label: 'Driver' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'picker_packer', label: 'Picker/Packer' },
  { value: 'sales', label: 'Sales' },
  { value: 'admin', label: 'Admin' },
  { value: 'other', label: 'Other' },
];

const AVAILABILITY_OPTIONS = [
  { value: 'full_time', label: 'Full-Time' },
  { value: 'part_time', label: 'Part-Time' },
  { value: 'seasonal', label: 'Seasonal' },
];

const CDL_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'A', label: 'Class A' },
  { value: 'B', label: 'Class B' },
];

export default function EmploymentApplication() {
  const [submitted, setSubmitted] = useState(false);
  const submitApp = useSubmitEmploymentApp();

  const form = useForm({
    initialValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: '',
      position_applied: '',
      desired_start_date: '',
      desired_pay: '',
      availability: 'full_time' as EmploymentApplicationType['availability'],
      has_food_safety_cert: false,
      has_drivers_license: false,
      cdl_class: '',
      previous_employer: '',
      previous_employer_phone: '',
      years_experience: undefined as number | undefined,
      references: '',
      notes: '',
    },
    validate: {
      first_name: (v) => (v.trim() ? null : 'First name is required'),
      last_name: (v) => (v.trim() ? null : 'Last name is required'),
      email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : 'A valid email is required'),
      phone: (v) => (v.trim() ? null : 'Phone number is required'),
      position_applied: (v) => (v ? null : 'Please select a position'),
    },
  });

  const handleSubmit = (values: typeof form.values) => {
    const payload: EmploymentApplicationType = {
      first_name: values.first_name.trim(),
      last_name: values.last_name.trim(),
      email: values.email.trim(),
      phone: values.phone.trim(),
      position_applied: values.position_applied,
      availability: values.availability,
      has_food_safety_cert: values.has_food_safety_cert,
      has_drivers_license: values.has_drivers_license,
      ...(values.address?.trim() && { address: values.address.trim() }),
      ...(values.desired_start_date?.trim() && { desired_start_date: values.desired_start_date.trim() }),
      ...(values.desired_pay?.trim() && { desired_pay: values.desired_pay.trim() }),
      ...(values.has_drivers_license && values.cdl_class && values.cdl_class !== 'none' && {
        cdl_class: values.cdl_class,
      }),
      ...(values.previous_employer?.trim() && { previous_employer: values.previous_employer.trim() }),
      ...(values.previous_employer_phone?.trim() && {
        previous_employer_phone: values.previous_employer_phone.trim(),
      }),
      ...(values.years_experience != null && { years_experience: values.years_experience }),
      ...(values.references?.trim() && { references: values.references.trim() }),
      ...(values.notes?.trim() && { notes: values.notes.trim() }),
    };

    submitApp.mutate(payload, {
      onSuccess: () => {
        setSubmitted(true);
      },
      onError: () => {
        notifications.show({
          title: 'Submission failed',
          message: 'Something went wrong. Please try again.',
          color: 'red',
        });
      },
    });
  };

  if (submitted) {
    return (
      <Container size={700} py="xl">
        <Stack align="center" gap="md" mt="xl">
          <img src="/logo.png" alt="Standard Produce Co." style={{ height: 80 }} />
          <Title order={2} ta="center">
            Thank You
          </Title>
          <Paper shadow="sm" p="xl" radius="md" w="100%">
            <Text ta="center" size="lg">
              Your application has been submitted. Our HR team will review it shortly.
            </Text>
          </Paper>
          <Anchor component={Link} to="/" size="sm">
            Back to Home
          </Anchor>
        </Stack>
      </Container>
    );
  }

  return (
    <Container size={700} py="xl">
      <Stack align="center" gap={4} mb="lg">
        <img src="/logo.png" alt="Standard Produce Co." style={{ height: 80 }} />
        <Title order={1} ta="center" mt="sm">
          Employment Application
        </Title>
        <Text c="dimmed" ta="center">
          Join the Standard Produce team
        </Text>
      </Stack>

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="lg">
          {/* Section 1: Personal Information */}
          <Paper shadow="sm" p="lg" radius="md">
            <Title order={4} mb="md">
              Personal Information
            </Title>
            <Stack gap="sm">
              <TextInput
                label="First Name"
                placeholder="First name"
                required
                {...form.getInputProps('first_name')}
              />
              <TextInput
                label="Last Name"
                placeholder="Last name"
                required
                {...form.getInputProps('last_name')}
              />
              <TextInput
                label="Email"
                placeholder="you@example.com"
                required
                {...form.getInputProps('email')}
              />
              <TextInput
                label="Phone"
                placeholder="(555) 123-4567"
                required
                {...form.getInputProps('phone')}
              />
              <Textarea
                label="Address"
                placeholder="Street address, city, state, ZIP"
                autosize
                minRows={2}
                {...form.getInputProps('address')}
              />
            </Stack>
          </Paper>

          {/* Section 2: Position Details */}
          <Paper shadow="sm" p="lg" radius="md">
            <Title order={4} mb="md">
              Position Details
            </Title>
            <Stack gap="sm">
              <Select
                label="Position Applied For"
                placeholder="Select a position"
                data={POSITION_OPTIONS}
                required
                {...form.getInputProps('position_applied')}
              />
              <TextInput
                label="Desired Start Date"
                placeholder="YYYY-MM-DD"
                {...form.getInputProps('desired_start_date')}
              />
              <TextInput
                label="Desired Pay/Salary"
                placeholder="e.g. $18/hr or $45,000/year"
                {...form.getInputProps('desired_pay')}
              />
              <Select
                label="Availability"
                data={AVAILABILITY_OPTIONS}
                {...form.getInputProps('availability')}
              />
            </Stack>
          </Paper>

          {/* Section 3: Qualifications */}
          <Paper shadow="sm" p="lg" radius="md">
            <Title order={4} mb="md">
              Qualifications
            </Title>
            <Stack gap="sm">
              <Checkbox
                label="Food Safety Certification"
                {...form.getInputProps('has_food_safety_cert', { type: 'checkbox' })}
              />
              <Checkbox
                label="Driver's License"
                {...form.getInputProps('has_drivers_license', { type: 'checkbox' })}
              />
              {form.values.has_drivers_license && (
                <Select
                  label="CDL Class"
                  placeholder="Select CDL class"
                  data={CDL_OPTIONS}
                  {...form.getInputProps('cdl_class')}
                />
              )}
              <NumberInput
                label="Years of Produce/Food Industry Experience"
                placeholder="0"
                min={0}
                max={99}
                {...form.getInputProps('years_experience')}
              />
            </Stack>
          </Paper>

          {/* Section 4: Work History */}
          <Paper shadow="sm" p="lg" radius="md">
            <Title order={4} mb="md">
              Work History
            </Title>
            <Stack gap="sm">
              <TextInput
                label="Previous Employer Name"
                placeholder="Company name"
                {...form.getInputProps('previous_employer')}
              />
              <TextInput
                label="Previous Employer Phone"
                placeholder="(555) 123-4567"
                {...form.getInputProps('previous_employer_phone')}
              />
              <Textarea
                label="References"
                placeholder="Name, relationship, and contact info for each reference"
                autosize
                minRows={3}
                {...form.getInputProps('references')}
              />
            </Stack>
          </Paper>

          {/* Section 5: Additional Notes */}
          <Paper shadow="sm" p="lg" radius="md">
            <Title order={4} mb="md">
              Additional Notes
            </Title>
            <Textarea
              label="Notes"
              placeholder="Anything else you would like us to know"
              autosize
              minRows={3}
              {...form.getInputProps('notes')}
            />
          </Paper>

          <Button type="submit" fullWidth size="md" loading={submitApp.isPending}>
            Submit Application
          </Button>
        </Stack>
      </form>

      <Text ta="center" mt="lg">
        <Anchor component={Link} to="/" size="sm">
          Back to Home
        </Anchor>
      </Text>
    </Container>
  );
}
